import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContractAccountingStatusModel, ContractsRepository } from '@/Contract/domain'
import { contractTranslation } from '@/Contract/ui/contractTranslation'
import { useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { useWebTagPermissions } from '@/User/context'
import { WebTagPermissionEnum } from '@/Shared/domain'

export interface IUseContractAccountingStatusController {
  accountingStatus: ContractAccountingStatusModel
  processUnpaidInvoices: () => Promise<boolean>
  clearDebt: () => Promise<void>
}

export const useContractAccountingStatusController = (
  id: number,
  repository: ContractsRepository,
): IUseContractAccountingStatusController => {
  const { t } = useTranslation()

  const { startLoading, stopLoading } = useLoader()
  const { handleMetaResponse } = useMetaResponseHandler()
  const { hasPermission, permissions } = useWebTagPermissions();

  const [accountingStatus, setAccountingStatus] = useState<ContractAccountingStatusModel>(
    {
      debtAmount: 0,
      isBlocked: false,
    },
  )

  const loadAccountingStatus = async (): Promise<void> => {
    if (!id) return
    startLoading()
    const response = await repository.GetAccountingStatus(id)

    stopLoading()

    if (handleMetaResponse(response.meta, undefined, { notifySuccess: false })) {
      setAccountingStatus({
        debtAmount: response.data?.debtAmount ?? 0,
        isBlocked: response.data?.isBlocked ?? false,
      })
    }
  }

  const processUnpaidInvoices = async (): Promise<boolean> => {
    if (!id) return false
    startLoading()

    const response = await repository.ProcessUnpaidInvoices(id)

    stopLoading()

    const valid = handleMetaResponse(response.meta, undefined, {
      notifySuccess: true,
      successMessage: t(contractTranslation.accountingStatus.processUnpaidInvoicesExit),
    })

    if (valid) {
      clearDebt()
    }

    return valid
  }

  const clearDebt = async (): Promise<void> => {
    setAccountingStatus({
      debtAmount: 0,
      isBlocked: false,
    })
  }

  useEffect(() => {
    if (id && hasPermission(WebTagPermissionEnum.ACTION_BANK_ACCOUNT_CHANGE)) {
      loadAccountingStatus()
    }
  }, [permissions])

  return {
    accountingStatus,
    processUnpaidInvoices,
    clearDebt,
  }
}
